import { VideoScriptSelectionDialog } from "@/components/RecordSelectionDialog/VideoScriptSelectionDialog";
import { Button, Card, Dialog } from "@/components/ui";
import { CoachSettings, WebSettings } from "@/interfaces";
import { Media, VideoScript } from "@/services/bedrock";
import { useAccountSettings, usePlayerSettings, useSocialCoachSession } from "@/utils";
import React, { useEffect, useMemo, useState } from "react";
import VideoPageUploader from "./VideoPageUploader";
import RecordVideoDialog, { RecordVideoType } from "@/containers/RecordVideo/RecordVideoDialog";

type Option = {
  key: string;
  title: string;
  description: string;
  action: () => void;
};

interface Props {
  open: boolean;
  title?: string;
  hideScriptSelection?: boolean;
  videoScript?: VideoScript | null;
  onVideoScriptSelected?: (script: VideoScript) => void;
  onOpenChange?: (open: boolean) => void;
  onMediaSelected: (media: Media) => void;
}

export const VideoPageSourceModal: React.FC<Props> = ({
  open,
  title = "New Video Page",
  hideScriptSelection = false,
  videoScript: initialVideoScript,
  onVideoScriptSelected,
  onOpenChange,
  onMediaSelected,
}) => {
  const [showUploader, setShowUploader] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [videoScriptModalOpen, setVideoScriptModalOpen] = useState(false);
  const [recordVideoType, setRecordVideoType] = useState<RecordVideoType>();
  const [videoScript, setVideoScript] = useState(initialVideoScript);

  useEffect(() => {
    setVideoScript(initialVideoScript);
  }, [initialVideoScript]);

  const { playerId, isPlayer } = useSocialCoachSession();
  const { hasAccountSetting } = useAccountSettings();
  const { hasSetting } = usePlayerSettings(playerId);
  const hasScriptLibrary = hasAccountSetting(WebSettings.MANAGE_SCRIPT_LIBRARY);
  const hasScreenCapture = useMemo(() => {
    if (!isPlayer) return true;
    return hasSetting(CoachSettings.ALLOW_PLAYER_TO_SCREEN_CAPTURE, true);
  }, [hasSetting, isPlayer]);

  const getOptionCard = (option: Option) => {
    return (
      <Card
        key={`option-${option.key}`}
        className="cursor-pointer hover:shadow-lg flex flex-col gap-2 p-3 w-50"
        onClick={() => option.action()}
      >
        <Card.Header className="p-0">
          <Card.Title className="text-lg">{option.title}</Card.Title>
        </Card.Header>
        <Card.Content className="p-0 text-muted-foreground">{option.description}</Card.Content>
      </Card>
    );
  };

  const recordYourselfOption: Option = {
    key: "recordYourself",
    title: "Record yourself",
    description: "Use your webcam to record a video",
    action: () => setRecordVideoType("teleprompter"),
  };
  const recordYourScreenOption: Option = {
    key: "recordYourScreen",
    title: "Record your screen",
    description: "Capture your screen, voice, and face simultaneously",
    action: () => setRecordVideoType("screen"),
  };
  const chooseAScriptOption: Option = {
    key: "chooseAScript",
    title: "Choose a Script",
    description: "Browse the script library to use with the teleprompter",
    action: () => setVideoScriptModalOpen(true),
  };
  const uploadAVideoOption: Option = {
    key: "uploadAVideo",
    title: "Upload a video",
    description: "Choose a video from your computer",
    action: () => {
      setShowUploader(true);
      setTimeout(() => setShowUploader(false), 500);
    },
  };

  const optionList = useMemo(() => {
    const options: Option[] = [];
    if (hasScriptLibrary) {
      options.push(recordYourselfOption);
      if (hasScreenCapture) {
        options.push(recordYourScreenOption);
      }
      if (!hideScriptSelection) {
        options.push(chooseAScriptOption);
      }
    }
    options.push(uploadAVideoOption);
    return options;
  }, [
    hasScriptLibrary,
    recordYourselfOption,
    hasScreenCapture,
    recordYourScreenOption,
    hideScriptSelection,
    chooseAScriptOption,
    uploadAVideoOption,
  ]);

  return (
    <>
      <VideoScriptSelectionDialog
        open={videoScriptModalOpen && open}
        onOpenChange={setVideoScriptModalOpen}
        defaultSelectedItems={videoScript ? [videoScript] : []}
        title="Select a Video Script"
        description="Select a Video Script to use for your Video Page."
        onSelect={script => {
          setVideoScript(script);
          setRecordVideoType("teleprompter");
          setVideoScriptModalOpen(false);
          onVideoScriptSelected?.(script);
        }}
      />
      <Dialog
        open={open && !videoScriptModalOpen}
        size="xl"
        disableEscapeDismissal={uploading}
        disablePointerDismissal={uploading}
        onOpenChange={onOpenChange}
      >
        <Dialog.Header>
          <Dialog.Title>{title}</Dialog.Title>
          <Dialog.Description>Select the source for your Video Page</Dialog.Description>
        </Dialog.Header>
        <Dialog.Content className="flex items-stretch justify-center gap-4">
          <VideoPageUploader show={showUploader} onUploading={setUploading} onUploaded={onMediaSelected} />
          {!uploading && optionList.map(option => getOptionCard(option))}
        </Dialog.Content>
        <Dialog.Footer>
          <Dialog.Close render={<Button type="button" variant="outline" disabled={uploading} children="Cancel" />} />
        </Dialog.Footer>
      </Dialog>
      <RecordVideoDialog
        open={!!recordVideoType}
        initialScript={videoScript?.content}
        initialRecordVideoType={recordVideoType ?? "teleprompter"}
        onVideoScriptSelected={onVideoScriptSelected}
        onOpenChange={open => {
          if (!open) setRecordVideoType(undefined);
        }}
        onDone={media => {
          onMediaSelected(media);
          setRecordVideoType(undefined);
        }}
      />
    </>
  );
};
